import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "quickstart-for-pdf-accessibility-auto-tag-api-java"
    }}>{`Quickstart for PDF Accessibility Auto-Tag API (Java)`}</h1>
    <p>{`To get started using Adobe PDF Accessibility Auto-Tag API, let's walk through a simple scenario - taking an input PDF document and running PDF Accessibility Auto-Tag API against it. Once the PDF has been tagged, we'll provide the document with tags and optionally, a report file. In this guide, we will walk you through the complete process for creating a program that will accomplish this task.`}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`To complete this guide, you will need:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.oracle.com/technetwork/java/javase/downloads/index.html"
        }}>{`Java`}</a>{` - Java 11 or higher is required. `}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://maven.apache.org/install.html"
        }}>{`Maven`}</a></li>
      <li parentName="ul">{`An Adobe ID. If you do not have one, the credential setup will walk you through creating one.`}</li>
      <li parentName="ul">{`A way to edit code. No specific editor is required for this guide.`}</li>
    </ul>
    <h2 {...{
      "id": "step-one-getting-credentials"
    }}>{`Step One: Getting credentials`}</h2>
    <p>{`1) To begin, open your browser to `}<a parentName="p" {...{
        "href": "https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-accessibility-auto-tag-api"
      }}>{`https://acrobatservices.adobe.com/dc-integration-creation-app-cdn/main.html?api=pdf-accessibility-auto-tag-api`}</a>{`. If you are not already logged in to Adobe.com, you will need to sign in or create a new user. Using a personal email account is recommend and not a federated ID.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "750px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "74.6875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/5530d/shot1.webp 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0c8fb/shot1.webp 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/3ea8a/shot1.webp 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/dd4a7/shot1.png 320w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/0f09e/shot1.png 640w", "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png 750w"],
            "sizes": "(max-width: 750px) 100vw, 750px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/3b0d32040b6b20e4343cb27657c7b00a/e9985/shot1.png",
            "alt": "Sign in",
            "title": "Sign in",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) After registering or logging in, you will then be asked to name your new credentials. Use the name, "New Project". `}</p>
    <p>{`3) Change the "Choose language" setting to "Java". `}</p>
    <p>{`4) Also note the checkbox by, "Create personalized code sample." This will include a large set of samples along with your credentials. These can be helpful for learning more later. `}</p>
    <p>{`5) Click the checkbox saying you agree to the developer terms and then click "Create credentials."`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "66.875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/5530d/shot2_spc.webp 320w", "/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/0c8fb/shot2_spc.webp 640w", "/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/94b1e/shot2_spc.webp 1280w", "/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/9ec20/shot2_spc.webp 1874w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/dd4a7/shot2_spc.png 320w", "/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/0f09e/shot2_spc.png 640w", "/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/bbbf7/shot2_spc.png 1280w", "/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/3261a/shot2_spc.png 1874w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/7dc9aa8c9855853ac98f44b2f748a8fe/bbbf7/shot2_spc.png",
            "alt": "Project setup",
            "title": "Project setup",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`6) After your credentials are created, they are automatically  downloaded:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "42.8125%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/5530d/shot3_spc.webp 320w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/0c8fb/shot3_spc.webp 640w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/94b1e/shot3_spc.webp 1280w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/09047/shot3_spc.webp 1876w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/dd4a7/shot3_spc.png 320w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/0f09e/shot3_spc.png 640w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/bbbf7/shot3_spc.png 1280w", "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/bd320/shot3_spc.png 1876w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/0604eb7f8f38efc5970b0310665692b9/bbbf7/shot3_spc.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "step-two-setting-up-the-project"
    }}>{`Step Two: Setting up the project`}</h2>
    <p>{`1) In your Downloads folder, find the ZIP file with your credentials: PDFServicesSDK-JavaSamples.zip. If you unzip that archive, you will find a folder of samples and the `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "452px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "67.1875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/5530d/shot5_spc.webp 320w", "/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/a0edd/shot5_spc.webp 452w"],
            "sizes": "(max-width: 452px) 100vw, 452px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/dd4a7/shot5_spc.png 320w", "/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/80d88/shot5_spc.png 452w"],
            "sizes": "(max-width: 452px) 100vw, 452px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/ab2017d1b9d12b9ba06277b11d5f9f0b/80d88/shot5_spc.png",
            "alt": "alt",
            "title": "alt",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`2) Take `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file and place it in a new directory.`}</p>
    <p>{`3) In this directory, create a new file named `}<inlineCode parentName="p">{`pom.xml`}</inlineCode>{` and copy the following content:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<?xml version="1.0" encoding="UTF-8"?>

<project xmlns="http://maven.apache.org/POM/4.0.0"
         xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
         xsi:schemaLocation="http://maven.apache.org/POM/4.0.0 http://maven.apache.org/xsd/maven-4.0.0.xsd">
  <modelVersion>4.0.0</modelVersion>

  <groupId>com.adobe.documentservices</groupId>
  <artifactId>pdfservices-sdk-autotag-guide</artifactId>
  <version>1</version>

  <name>PDF Services Java SDK Samples</name>

  <properties>
    <project.build.sourceEncoding>UTF-8</project.build.sourceEncoding>
    <maven.compiler.source>11</maven.compiler.source>
    <maven.compiler.target>11</maven.compiler.target>
    <pdfservices.sdk.version>4.3.0</pdfservices.sdk.version>
  </properties>

  <dependencies>

    <dependency>
      <groupId>com.adobe.documentservices</groupId>
      <artifactId>pdfservices-sdk</artifactId>
      <version>\${pdfservices.sdk.version}</version>
    </dependency>

    <!--        log4j2 dependency to showcase the use of log4j2 with slf4j API-->
    <!-- https://mvnrepository.com/artifact/org.slf4j/slf4j-log4j12 -->
    <dependency>
      <groupId>org.apache.logging.log4j</groupId>
      <artifactId>log4j-slf4j-impl</artifactId>
      <version>2.21.1</version>
    </dependency>
  </dependencies>

  <build>
    <plugins>
      <plugin>
        <groupId>org.apache.maven.plugins</groupId>
        <artifactId>maven-compiler-plugin</artifactId>
        <version>3.8.0</version>
        <configuration>
          <source>\${maven.compiler.source}</source>
          <target>\${maven.compiler.target}</target>
        </configuration>
      </plugin>
      <plugin>
        <groupId>org.codehaus.mojo</groupId>
        <artifactId>exec-maven-plugin</artifactId>
        <version>1.5.0</version>
        <executions>
          <execution>
            <goals>
              <goal>java</goal>
            </goals>
          </execution>
        </executions>
      </plugin>
    </plugins>
  </build>
</project>
`}</code></pre>
    <p>{`This file will define what dependencies we need and how the application will be built. `}</p>
    <p>{`Our application will take a PDF, `}<inlineCode parentName="p">{`Adobe_Accessibility_Auto_Tag_API_Sample.pdf`}</inlineCode>{` (downloadable from `}<a href="/document-services/docs/assets/b3b8ca5aad09665ff577cffb35ad98cf/Adobe_Accessibility_Auto_Tag_API_Sample.pdf" target="_blank">{`here`}</a>{`) and tag its contents. The results will be saved in the directory `}<inlineCode parentName="p">{`/output`}</inlineCode>{`.`}</p>
    <p>{`4) In your editor, open the directory where you previously copied the credentials, and create a new directory, `}<inlineCode parentName="p">{`src/main/java`}</inlineCode>{`. In that directory, create `}<inlineCode parentName="p">{`AutotagPDF.java`}</inlineCode>{`. `}</p>
    <p>{`Now you're ready to begin coding.`}</p>
    <h2 {...{
      "id": "step-three-creating-the-application"
    }}>{`Step Three: Creating the application`}</h2>
    <p>{`1) We'll begin by including our required dependencies:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import com.adobe.pdfservices.operation.PDFServices;
import com.adobe.pdfservices.operation.PDFServicesMediaType;
import com.adobe.pdfservices.operation.PDFServicesResponse;
import com.adobe.pdfservices.operation.auth.Credentials;
import com.adobe.pdfservices.operation.auth.ServicePrincipalCredentials;
import com.adobe.pdfservices.operation.exception.SdkException;
import com.adobe.pdfservices.operation.exception.ServiceApiException;
import com.adobe.pdfservices.operation.exception.ServiceUsageException;
import com.adobe.pdfservices.operation.io.Asset;
import com.adobe.pdfservices.operation.io.StreamAsset;
import com.adobe.pdfservices.operation.pdfjobs.jobs.AutotagPDFJob;
import com.adobe.pdfservices.operation.pdfjobs.result.AutotagPDFResult;
import org.apache.commons.io.IOUtils;
import org.slf4j.Logger;
import org.slf4j.LoggerFactory;

import java.io.File;
import java.io.IOException;
import java.io.InputStream;
import java.io.OutputStream;
import java.nio.file.Files;
import java.nio.file.Paths;
`}</code></pre>
    <p>{`2) Now let's define our main class:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`public class AutotagPDF {

    private static final Logger LOGGER = LoggerFactory.getLogger(AutotagPDF.class);

    public static void main(String[] args) {

    }
}
`}</code></pre>
    <p>{`3) Set the environment variables `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PDF_SERVICES_CLIENT_SECRET`}</inlineCode>{` by running the following commands and replacing placeholders `}<inlineCode parentName="p">{`YOUR CLIENT ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`YOUR CLIENT SECRET`}</inlineCode>{` with the credentials present in `}<inlineCode parentName="p">{`pdfservices-api-credentials.json`}</inlineCode>{` file:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Windows:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`set PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`MacOS/Linux:`}</strong></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_ID=<YOUR CLIENT ID>`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`export PDF_SERVICES_CLIENT_SECRET=<YOUR CLIENT SECRET>`}</inlineCode></li>
        </ul>
      </li>
    </ul>
    <p>{`4) Next, we can create our credentials and PDFServices instance:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Initial setup, create credentials instance
Credentials credentials = new ServicePrincipalCredentials(
        System.getenv("PDF_SERVICES_CLIENT_ID"),
        System.getenv("PDF_SERVICES_CLIENT_SECRET"));

// Creates a PDF Services instance
PDFServices pdfServices = new PDFServices(credentials);
`}</code></pre>
    <p>{`5) Now, let's upload the asset:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`Asset asset = pdfServices.upload(inputStream, PDFServicesMediaType.PDF.getMediaType());
`}</code></pre>
    <p>{`We define input stream for the PDF that will be tagged. (You can download the source we used `}<a href="/document-services/docs/assets/b3b8ca5aad09665ff577cffb35ad98cf/Adobe_Accessibility_Auto_Tag_API_Sample.pdf" target="_blank">{`here`}</a>{`.) In a real application, these values would be typically be dynamic.
Then we upload the content of input stream and specify the input media type as PDF.`}</p>
    <p>{`6) Now, let's create the job and parameters:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Create parameters for the job
AutotagPDFParams autotagPDFParams = AutotagPDFParams.autotagPDFParamsBuilder().generateReport().shiftHeadings().build();

// Creates a new job instance
AutotagPDFJob autotagPDFJob = new AutotagPDFJob(asset).setParams(autotagPDFParams);
`}</code></pre>
    <p>{`This set of code defines what we're doing (an Auto-Tag operation),
it defines parameters for the Auto-Tag job. PDF Accessibility Auto-Tag API has a few different options, but in this example, we're simply asking for a basic tagging operation, which returns the tagged PDF document and an XLSX report of the document.`}</p>
    <p>{`7) The next code block submits the job and gets the job result:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Submit the job and get the job result
String location = pdfServices.submit(autotagPDFJob);
PDFServicesResponse<AutotagPDFResult> pdfServicesResponse = pdfServices.getJobResult(location, AutotagPDFResult.class);

// Get content from the resulting asset(s)
Asset resultAsset = pdfServicesResponse.getResult().getTaggedPDF();
Asset resultAssetReport = pdfServicesResponse.getResult().getReport();
StreamAsset streamAsset = pdfServices.getContent(resultAsset);
StreamAsset streamAssetReport = pdfServices.getContent(resultAssetReport);
`}</code></pre>
    <p>{`8) The next code block saves the result at the specified location:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// Creating output streams and copying stream assets' content to it
OutputStream outputStream = Files.newOutputStream(new File("output/autotag-tagged.pdf").toPath());
OutputStream outputStreamReport = Files.newOutputStream(new File("output/autotag-report.xlsx").toPath());
IOUtils.copy(streamAsset.getInputStream(), outputStream);
IOUtils.copy(streamAssetReport.getInputStream(), outputStreamReport);
`}</code></pre>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "28.750000000000004%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/5530d/shot9_ga.webp 320w", "/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/0c8fb/shot9_ga.webp 640w", "/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/94b1e/shot9_ga.webp 1280w", "/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/89420/shot9_ga.webp 1758w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/dd4a7/shot9_ga.png 320w", "/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/0f09e/shot9_ga.png 640w", "/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/bbbf7/shot9_ga.png 1280w", "/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/cbcea/shot9_ga.png 1758w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/document-services/docs/static/ca6802b6c607bb8cca94ff8ce8cccdfd/bbbf7/shot9_ga.png",
            "alt": "Example running in the command line",
            "title": "Example running in the command line",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`Here's the complete application (`}<inlineCode parentName="p">{`src/main/java/AutotagPDF.java`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import com.adobe.pdfservices.operation.PDFServices;
import com.adobe.pdfservices.operation.PDFServicesMediaType;
import com.adobe.pdfservices.operation.PDFServicesResponse;
import com.adobe.pdfservices.operation.auth.Credentials;
import com.adobe.pdfservices.operation.auth.ServicePrincipalCredentials;
import com.adobe.pdfservices.operation.exception.SDKException;
import com.adobe.pdfservices.operation.exception.ServiceApiException;
import com.adobe.pdfservices.operation.exception.ServiceUsageException;
import com.adobe.pdfservices.operation.io.Asset;
import com.adobe.pdfservices.operation.io.StreamAsset;
import com.adobe.pdfservices.operation.pdfjobs.jobs.AutotagPDFJob;
import com.adobe.pdfservices.operation.pdfjobs.params.autotag.AutotagPDFParams;
import com.adobe.pdfservices.operation.pdfjobs.result.AutotagPDFResult;
import org.apache.commons.io.IOUtils;
import org.slf4j.Logger;
import org.slf4j.LoggerFactory;

import java.io.File;
import java.io.IOException;
import java.io.InputStream;
import java.io.OutputStream;
import java.nio.file.Files;
import java.nio.file.Paths;

public class AutotagPDF {
  // Initialize the logger
  private static final Logger LOGGER = LoggerFactory.getLogger(AutotagPDF.class);

  public static void main(String[] args) {
    try (InputStream inputStream = Files.newInputStream(new File("src/main/resources/Adobe_Accessibility_Auto_Tag_API_Sample.pdf").toPath())) {
      // Initial setup, create credentials instance
      Credentials credentials = new ServicePrincipalCredentials(
              System.getenv("PDF_SERVICES_CLIENT_ID"),
              System.getenv("PDF_SERVICES_CLIENT_SECRET"));
    
      // Creates a PDF Services instance
      PDFServices pdfServices = new PDFServices(credentials);
    
      // Creates an asset(s) from source file(s) and upload
      Asset asset = pdfServices.upload(inputStream, PDFServicesMediaType.PDF.getMediaType());
    
      // Create parameters for the job
      AutotagPDFParams autotagPDFParams = AutotagPDFParams.autotagPDFParamsBuilder().generateReport().shiftHeadings().build();
    
      // Creates a new job instance
      AutotagPDFJob autotagPDFJob = new AutotagPDFJob(asset).setParams(autotagPDFParams);
    
      // Submit the job and gets the job result
      String location = pdfServices.submit(autotagPDFJob);
      PDFServicesResponse<AutotagPDFResult> pdfServicesResponse = pdfServices.getJobResult(location, AutotagPDFResult.class);
    
      // Get content from the resulting asset(s)
      Asset resultAsset = pdfServicesResponse.getResult().getTaggedPDF();
      Asset resultAssetReport = pdfServicesResponse.getResult().getReport();
      StreamAsset streamAsset = pdfServices.getContent(resultAsset);
      StreamAsset streamAssetReport = pdfServices.getContent(resultAssetReport);
      
      // Creating output streams and copying stream assets' content to it
      Files.createDirectories(Paths.get("output/"));
      OutputStream outputStream = Files.newOutputStream(new File("output/autotag-tagged.pdf").toPath());
      OutputStream outputStreamReport = Files.newOutputStream(new File("output/autotag-report.xlsx").toPath());
      LOGGER.info("Saving asset at output/autotag-tagged.pdf");
      LOGGER.info("Saving asset at output/autotag-report.xlsx");
      IOUtils.copy(streamAsset.getInputStream(), outputStream);
      IOUtils.copy(streamAssetReport.getInputStream(), outputStreamReport);
      outputStream.close();
      outputStreamReport.close();
    } catch (ServiceApiException | IOException | SDKException | ServiceUsageException ex) {
      LOGGER.error("Exception encountered while executing operation", ex);
    }
  }
}
`}</code></pre>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next Steps`}</h2>
    <p>{`Now that you've successfully performed your first operation, `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/document-services/docs/overview/pdf-services-api/"
      }}>{`review the documentation`}</a>{` for many other examples and reach out on our `}<a parentName="p" {...{
        "href": "https://community.adobe.com/t5/document-services-apis/ct-p/ct-Document-Cloud-SDK"
      }}>{`forums`}</a>{` with any questions. Also remember the samples you downloaded while creating your credentials also have many demos.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      